/** @jsxImportSource @emotion/react */
import { Alert, Box, Button, Typography, useTheme } from '@mui/material'
import { memo, useEffect, useState } from 'react'
import { Delete } from '@mui/icons-material'
import { useSetAtom } from 'jotai'
import { useMap } from '@app/contexts/ModuleMapContexts/RiskMapContext'
import { Accordion } from '../../../../../../Atoms/Accordion'
import { EmptySection } from '../../../../components/EmptySection'
import { SelectedLiveFeature } from '@app/modules/Live/Live.types'
import { infoLayerFocusAtom } from '@app/state/layerAtom'
import {
  liveSelectedClearButton,
  liveSelectedContent,
  liveSelectedDetailContainer,
  liveSelectedHeader,
  liveSelectedImage,
  liveSelectedLastUpdated,
  liveSelectedProperty,
  liveSelectedPropertyRow,
  liveSelectedSection,
} from './LiveSelectedTabBody.styles'
import { buildLiveFeatureSection } from './LiveSelectedTabBody.utils'

export interface LiveFeatureRow {
  label: string
  value: string | number
  type?: string
}

export interface LiveFeatureSection {
  layerName: string
  lastUpdated: Date | null
  isStale: boolean
  rows: LiveFeatureRow[]
}

export interface LiveSelectedTabBodyProps {
  selectedLiveFeatures: SelectedLiveFeature[]
}

export const LiveSelectedTabBody = memo(function LiveSelectedTabBody({
  selectedLiveFeatures,
}: LiveSelectedTabBodyProps) {
  const theme = useTheme()
  const { deselectFeature } = useMap()
  const setInfoLayerFocus = useSetAtom(infoLayerFocusAtom)
  const [isLoading, setIsLoading] = useState(false)
  const [sections, setSections] = useState<LiveFeatureSection[] | null>(null)

  useEffect(() => {
    if (selectedLiveFeatures.length === 0) {
      setSections(null)
      return
    }

    let cancelled = false
    setIsLoading(true)

    Promise.all(
      selectedLiveFeatures.map(({ feature, liveLayer }) =>
        buildLiveFeatureSection(
          (feature.properties ?? {}) as Record<string, string | number>,
          liveLayer,
        ),
      ),
    )
      .then((result) => {
        if (!cancelled) {
          setSections(result)
          setIsLoading(false)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSections(null)
          setIsLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [selectedLiveFeatures])

  if (isLoading) {
    return (
      <EmptySection title="Loading details on selected element" isLoading={true} height="240px" />
    )
  }

  if (!sections) return null

  const isMulti = sections.length > 1

  return (
    <Box css={liveSelectedDetailContainer({ theme })}>
      <Box css={liveSelectedHeader({ theme })}>
        <Typography variant="h6" component="div" color={'primary'}>
          <strong>Selected Live Layer Details</strong>
        </Typography>
        <Button
          onClick={(e) => {
            e.stopPropagation()
            deselectFeature()
            setInfoLayerFocus('layers')
          }}
          variant="outlined"
          size="small"
          css={liveSelectedClearButton}
        >
          Clear selection <Delete />
        </Button>
      </Box>

      <Box css={liveSelectedContent({ theme })}>
        {isMulti && (
          <Alert severity="info">
            One or more selected layers are overlapping. Selected layers are shown below.
          </Alert>
        )}
        {sections.map((section, sectionIndex) => (
          <Accordion
            variant="filled"
            level="h3"
            key={section.layerName + sectionIndex}
            header={<Typography>{section.layerName}</Typography>}
            canCollapse={false}
            toggleButton={true}
            body={
              <Box css={liveSelectedSection({ theme })}>
                {section.isStale && (
                  <Alert severity="warning">This layer was last updated over 24 hours ago</Alert>
                )}
                {section.lastUpdated && (
                  <Box css={liveSelectedLastUpdated({ theme })}>
                    <Typography variant="body1">
                      Last Updated: {section.lastUpdated.toLocaleString()}
                    </Typography>
                  </Box>
                )}
                <Box css={liveSelectedProperty({ theme })}>
                  {section.rows.map((row, rowIndex) =>
                    row.type === 'image' ? (
                      <Box
                        key={rowIndex}
                        component="img"
                        src={String(row.value)}
                        alt={row.label}
                        css={liveSelectedImage({ theme })}
                      />
                    ) : (
                      <Box key={rowIndex} css={liveSelectedPropertyRow({ theme })}>
                        <Typography variant="body1">
                          <strong>{row.label}:</strong> {row.value}
                        </Typography>
                      </Box>
                    ),
                  )}
                </Box>
              </Box>
            }
          />
        ))}
      </Box>
    </Box>
  )
})
